import { useEffect, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { TEMPORARY_ID_PREFIX, useUpdateAction } from '@/models/article';
import { ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionContextMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const actionOrPreviousAction = action ?? previousAction!;

  if (!actionOrPreviousAction.context) {
    return null;
  }

  return (
    <Stack
      gap="0.25rem"
      className={element('card-context', {
        edited: getEditedFields({ action, previousAction }).isContextEdited,
      })}
    >
      <p className="label-200-regular">
        <Trans id="article.form.actions.card.context.label">Context:</Trans>
      </p>
      <Paragraph>&ldquo;{actionOrPreviousAction.context}&rdquo;</Paragraph>
    </Stack>
  );
};

export const ActionContextInput = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { t } = useLingui();
  const { request, article, view, errors } = useArticleContext();

  const isEditable =
    !!action &&
    view.services.actions.fields.context.editable &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX);
  const isRequired = view.services.actions.fields.context.required;

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateAction = useMemo(() => {
    return debounce(updateAction, 500);
  }, [updateAction]);

  const actionOrPreviousAction = action ?? previousAction;
  const [context, setContext] = useState(actionOrPreviousAction?.context ?? '');

  useEffect(() => {
    setContext(actionOrPreviousAction?.context ?? '');
    debouncedUpdateAction.cancel();
  }, [actionOrPreviousAction?.context, debouncedUpdateAction]);

  if (!actionOrPreviousAction?.context && !isEditable) {
    return null;
  }

  return (
    <TextArea
      label={
        <>
          <Trans id="article.form.actions.context.label">Context</Trans>
          {isEditable && !isRequired && <OptionalLabel />}
        </>
      }
      isInvalid={
        errors?.services?.actions?.actions?.find(({ id }) => id === action?.id)?.missingContext
      }
      placeholder={t({
        id: 'article.form.actions.context.placeholder',
        message: 'Add some context...',
      })}
      value={context}
      onChange={(event) => {
        if (action) {
          const currentContext = action.context;

          setContext(event.target.value);
          debouncedUpdateAction({
            actionId: action.id,
            body: {
              context: event.target.value.trim(),
            },
          })?.catch(() => setContext(currentContext ?? ''));
        }
      }}
      disabled={!isEditable}
      className={element('card-context', {
        edited: getEditedFields({ action, previousAction }).isContextEdited,
      })}
    />
  );
};
